/** Copyright (c) 2022 T.Rowe Price. All rights reserved. */

import { createSelector } from 'reselect';
import { pick } from 'lodash';
import { getView } from 'state/view/ViewSelectors';
import {
  getCustomer,
  getPlanningSummaryClientUberId,
  getPlanningSummaryCoClientUberId,
  getUberId,
  isRetirementAdvisoryServiceClient
} from 'state/customer/CustomerSelectors';
import { getAssetId } from 'state/household/HouseholdInformationSelectors';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';
import { booleanToYN, customerSegmentName } from 'support/StringUtil';
import { getAppEntryPoint } from '../session/SessionSelectors';
import { requestStateConfig } from 'components/layouts/Navigation/RequestStateConfig';

export const hasAppLoaded = createSelector(
  getAppEntryPoint,
  getRequestStatus,
  (entryPoint, requestStatus) => {
    return Object.values(requestStatus)
      .filter((status) => status !== REQUEST_STATES.error)
      .every((s) => s === REQUEST_STATES.received);
  }
);

export const hasAdobeData = createSelector(
  getRequestStatus,
  (requestStatus) => {
    return requestStatus.customer === REQUEST_STATES.received;
  }
);

export const hasCurrentRouteLoaded = createSelector(
  getAppEntryPoint,
  getRequestStatus,
  (entryPoint, requestStatus) => {
    const pageName =
      entryPoint && entryPoint.indexOf('/') == 0
        ? entryPoint.substr(1, entryPoint.length).toLowerCase()
        : '';
    const pageSpecificRequestStates = requestStateConfig[pageName];
    const allStatesAvailable =
      pageSpecificRequestStates &&
      pageSpecificRequestStates.every((ps) =>
        Object.keys(requestStatus).includes(ps)
      );
    if (allStatesAvailable) {
      const pageStatuses = pick(requestStatus, pageSpecificRequestStates);
      return Object.values(pageStatuses).every(
        (s) => s === REQUEST_STATES.received || s === REQUEST_STATES.error
      );
    }
    return false;
  }
);

export const hasAppError = createSelector(
  getAppEntryPoint,
  getRequestStatus,
  (entryPoint, requestStatus) => {
    const pageName =
      entryPoint && entryPoint.indexOf('/') == 0
        ? entryPoint.substr(1, entryPoint.length).toLowerCase()
        : '';
    const pageSpecificRequestStates = requestStateConfig[pageName];
    const pageStatuses = pick(requestStatus, pageSpecificRequestStates);
    return pageStatuses
      ? Object.values(pageStatuses).some(
          (status) => status === REQUEST_STATES.error
        )
      : false;
  }
);

export const getAdobeData = createSelector(
  [
    isRetirementAdvisoryServiceClient,
    getAssetId,
    getPlanningSummaryClientUberId,
    getPlanningSummaryCoClientUberId,
    getUberId
  ],
  (isRasClient, clientTier, clientUberId, coClientUberId, uberId) => {
    const isClient = uberId && uberId === clientUberId;
    const isCoClient = uberId && uberId === coClientUberId;
    const clientType = isClient ? 'client' : isCoClient ? 'co-client' : '';
    const data = {
      page: {
        lob: 'RET',
        site: 'ACCOUNT_ACCESS'
      },
      user: {},
      ras: {
        isActive: isRasClient || false,
        clientType
      }
    };
    // add this attribute only if available
    if (clientTier) {
      return {
        ...data,
        user: {
          clientTier
        }
      };
    }
    return data;
  }
);

export const hasQualtricsData = createSelector(
  getRequestStatus,
  (requestStatus) => {
    return (
      requestStatus.customer === REQUEST_STATES.received &&
      requestStatus.accounts === REQUEST_STATES.received
    );
  }
);

export const getQualtricsData = createSelector(
  getView,
  getCustomer,
  getAssetId,
  (view, customer, clientTier) => {
    const { firstName = '', lastName = '' } =
      customer.relationshipManager || {};
    const rmName = `${firstName} ${lastName}`.trim();
    const hasRM = rmName.length > 0;

    const data = {
      age: customer.age,
      custSeg: customerSegmentName(customer.serviceType),
      hasAPP: booleanToYN(view.hasActivePlus),
      hasBrokerage: booleanToYN(view.hasBrokerage),
      hasA529: booleanToYN(view.hasAlaska529),
      hasM529: booleanToYN(view.hasMaryland529),
      hasN529: booleanToYN(view.hasNational529),
      hasRetirement: booleanToYN(view.hasRetirementAccounts),
      hasRPS: booleanToYN(view.hasWorkplaceRetirementAccounts),
      hasRM: booleanToYN(hasRM),
      hasTaxable: booleanToYN(view.hasNonRetirementAccounts),
      hasRAS: booleanToYN(customer.isRetirementAdvisoryServiceClient),
      rmName: rmName
    };
    // add this attribute only if available
    if (clientTier) {
      return {
        ...data,
        clientTier
      };
    }
    return data;
  }
);
